/**
 * Shared desktop shell for onboarding (Society / Flat / Verify) — port of the
 * "Onboarding desktop" frame (design/redesign_v3/Auth & Onboarding.dc.html):
 * 380px steps rail (left, canvas1) + content pane (right). Breakpoint 1024.
 * Underscore-prefixed: expo-router ignores this file, it is not a route.
 */
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { BrandLockup } from '@/components/brand';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText } from '@/ui';

export const ONBOARDING_DESKTOP_BREAKPOINT = 1024;

export type StepState = 'done' | 'active' | 'upcoming';
export type Step = { label: string; sub: string; state: StepState };

function StepDot({ n, state, colors }: { n: number; state: StepState; colors: ReturnType<typeof useTheme>['colors'] }) {
  const bg = state === 'done' ? colors.primary500 : state === 'active' ? colors.accent100 : colors.canvas0;
  const border = state === 'done' ? colors.primary500 : state === 'active' ? colors.accent500 : colors.border;
  const ink = state === 'done' ? colors.canvas0 : state === 'active' ? colors.accent700 : colors.textTertiary;
  return (
    <View style={{
      width: 28, height: 28, borderRadius: 999, backgroundColor: bg,
      borderWidth: 1.5, borderColor: border, alignItems: 'center', justifyContent: 'center',
    }}>
      <AppText variant="bodySm" weight="600" style={{ color: ink }}>{state === 'done' ? '✓' : n}</AppText>
    </View>
  );
}

/** Steps rail — pass the current step's index via `steps[i].state`. */
export function StepsRail({ steps, footNote }: { steps: Step[]; footNote?: string }) {
  const { colors } = useTheme();
  const { t } = useT();
  return (
    <View style={{
      width: 380, flexGrow: 0, flexShrink: 0, backgroundColor: colors.canvas1,
      borderRightWidth: 1, borderRightColor: colors.borderSoft, padding: space.s10,
    }}>
      <BrandLockup markHeight={24} />
      <View style={{ marginTop: space.s12 }}>
        {steps.map((s, i) => (
          <View key={s.label} style={{ flexDirection: 'row', gap: 14 }}>
            <View style={{ alignItems: 'center' }}>
              <StepDot n={i + 1} state={s.state} colors={colors} />
              {i < steps.length - 1 ? (
                <View style={{ width: 1.5, flex: 1, minHeight: 28, backgroundColor: colors.border, opacity: s.state === 'upcoming' ? 0.4 : 1 }} />
              ) : null}
            </View>
            <View style={{ paddingBottom: space.s6 }}>
              <AppText variant="bodyMd" weight="600" tone={s.state === 'upcoming' ? 'tertiary' : 'primary'}>{s.label}</AppText>
              <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{s.sub}</AppText>
            </View>
          </View>
        ))}
      </View>
      <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 'auto', lineHeight: 18 }}>
        {footNote ?? t('onb_foot_note')}
      </AppText>
    </View>
  );
}

/** Full desktop shell: rail + scrollable content pane. No safe-area insets —
 * consistent with the already-approved login desktop split (web/desktop has
 * no device notches to account for). */
export function DesktopShell({ rail, children }: { rail: React.ReactNode; children: React.ReactNode }) {
  const { colors } = useTheme();
  return (
    <View style={{ flex: 1, flexDirection: 'row', backgroundColor: colors.canvas0 }}>
      {rail}
      <View style={{ flex: 1, padding: space.s12 }}>{children}</View>
    </View>
  );
}
